/**
 * Layout Constants - Constantes padronizadas para o layout da MainPage
 *
 * Hierarquia de referência (valores calibrados visualmente em 2026-01-05):
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │                           TOPBAR (64px)                             │
 * ├───────────────┬───────────────────────────────────────────────┬─────┤
 * │               │ [StatusCard]              [Geofence] [POI]    │CTRL │
 * │   SIDEBAR     │ (left=404, top=72)        (top=72)            │(40) │
 * │   (400px)     │                                               │     │
 * │               │                                               │ [+] │
 * │               │              MAP AREA                         │ [-] │
 * │               │                                               │ ... │
 * │               │                                               │     │
 * │               │                                              │     │
 * │               ├───────────────────────────────────────────────┤     │
 * │               │           DEVICE DETAIL PANEL (~120px)         │     │
 * │               │           left=364, right=4, bottom=4         │     │
 * └───────────────┴───────────────────────────────────────────────┴─────┘
 *     gap(4px)                   gap(4px)                      gap(4px)
 */

// Dimensões principais
export const TOPBAR_HEIGHT = 64;
export const TOPBAR_BUTTON_HEIGHT = 40; // Altura dos botões na TopBar
export const SIDEBAR_WIDTH = 400; // Aumentado de 360px para ocupar mais espaço
export const CONTROLS_WIDTH = 40;  // Largura real dos controles do mapa (zoom, layers)
export const DEVICE_DETAIL_HEIGHT = 120; // Altura real do DeviceDetailPanelPremium (calibrado: speedometer.bottom - GAP)
export const DEVICE_DETAIL_COLLAPSED_BUTTON_HEIGHT = 32; // Altura do botão de collapse quando painel recolhido

// Gaps padronizados (baseados em theme.spacing)
export const GAP = {
  TINY: 2,      // theme.spacing(0.25)
  SMALL: 4,     // theme.spacing(0.5) - gap padrão entre elementos
  MEDIUM: 8,    // theme.spacing(1) - gap entre seções
  LARGE: 16,    // theme.spacing(2) - gap grande
  XLARGE: 24,   // theme.spacing(3) - gap extra grande
};

// Dimensões dos widgets
export const WIDGET_DIMENSIONS = {
  statusCounters: { width: 100, height: 340 }, // Altura real do painel com todas as linhas de status
  poiFilter: { width: 220, height: 'auto' },
  geofenceFilter: { width: 220, height: 'auto' },
  speedometer: { width: 180, height: 220 },
  speedometerMinimized: { width: 100, height: 50 },
};

// Posições calculadas para widgets
export const WIDGET_POSITIONS = {
  // StatusCountersPanel - canto superior esquerdo do mapa
  // Posição calibrada visualmente para alinhar com o topo da sidebar
  // Valor: TOPBAR_HEIGHT (64) + offset (-58) = 6px do topo
  statusCounters: {
    top: TOPBAR_HEIGHT + -58, // 6px - calibrado visualmente
    left: SIDEBAR_WIDTH + GAP.SMALL, // 364px (após sidebar + 4px gap)
  },

  // StatusCard - topo do mapa, ao lado da sidebar (estilo MettaX)
  statusCard: {
    left: SIDEBAR_WIDTH + GAP.SMALL, // 404px (após sidebar + 4px gap)
    top: TOPBAR_HEIGHT - GAP.SMALL - 5, // 55px
  },

  // POI Filter Panel - canto superior direito, afastado dos controles
  // Posição: mais à esquerda para não colidir com MapControls
  poiFilter: {
    top: TOPBAR_HEIGHT + GAP.MEDIUM, // 72px (64 + 8)
    right: CONTROLS_WIDTH + GAP.XLARGE + GAP.MEDIUM, // 72px (40 + 24 + 8) - mais espaço dos controles
  },

  // Geofence Filter Panel - ao lado esquerdo do POI filter
  geofenceFilter: {
    top: TOPBAR_HEIGHT + GAP.MEDIUM, // 72px
    right: CONTROLS_WIDTH + GAP.XLARGE + GAP.MEDIUM + WIDGET_DIMENSIONS.poiFilter.width + GAP.MEDIUM, // 300px
  },

  // Speedometer - canto inferior direito, alinhado com DeviceDetailPanel
  // bottom: Calibrado visualmente para alinhar com topo do DeviceDetailPanel
  speedometer: {
    right: GAP.SMALL, // 4px da borda direita (igual DeviceDetailPanel)
    bottom: 122, // Calibrado visualmente - acima do DeviceDetailPanel (~80px + gaps)
  },
};

// Posições dos controles do mapa (zoom, layers, etc)
// Posicionados abaixo dos botões da TopBar
export const MAP_CONTROLS = {
  top: TOPBAR_BUTTON_HEIGHT + 6, // 46px - abaixo dos botões da TopBar (40px + 6px gap)
  right: GAP.SMALL,              // 4px da borda direita
  bottom: GAP.SMALL,             // 4px da borda inferior
};

// Dimensões dos painéis de filtro
export const FILTER_PANEL = {
  WIDTH: 220,
  MIN_HEIGHT: 200,
  MAX_HEIGHT: 400,
};

// Z-index hierarchy
export const Z_INDEX = {
  MAP: 1,
  SIDEBAR: 3,
  DEVICE_LIST: 4,
  MAP_CONTROLS: 5,
  WIDGETS: 1100,
  DEVICE_DETAIL: 1200,
  STATUS_CARD: 1201,
  MODALS: 1300,
  TOPBAR: 1400,
};

// Helper function to get CSS value with px
export const px = (value) => `${value}px`;

// Helper function to calculate position from sidebar
export const fromSidebar = (gap = GAP.SMALL) => SIDEBAR_WIDTH + GAP.SMALL + gap;

// Helper function to calculate position from top (below TopBar)
export const fromTopBar = (gap = GAP.SMALL) => TOPBAR_HEIGHT + gap;

// Helper function to calculate position from bottom (above DeviceDetailPanel)
export const fromDeviceDetail = (gap = GAP.SMALL) => DEVICE_DETAIL_HEIGHT + gap;

// Helper function to calculate position from right (left of map controls)
export const fromControls = (gap = GAP.SMALL) => CONTROLS_WIDTH + gap;

export default {
  TOPBAR_HEIGHT,
  TOPBAR_BUTTON_HEIGHT,
  SIDEBAR_WIDTH,
  CONTROLS_WIDTH,
  DEVICE_DETAIL_HEIGHT,
  DEVICE_DETAIL_COLLAPSED_BUTTON_HEIGHT,
  GAP,
  WIDGET_POSITIONS,
  MAP_CONTROLS,
  FILTER_PANEL,
  Z_INDEX,
  px,
  fromSidebar,
  fromTopBar,
  fromDeviceDetail,
  fromControls,
};
